import { useEffect, useMemo, useState, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Stack,
  Chip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getRegion } from "../../api/regionAPI";
import { getNodeWithStatus, getNode, getOnlineNodes as getOnlineNodesAPI } from "../../api/nodeAPI";
import {getDevices} from "../../api/deviceAPI";
import { renderOnline, renderOffline, renderAlarmm, renderWarningg, renderNormal } from "../../components/StatusChip";
import { useInterval } from "../../hooks/useInterval";
import ConvertOIDValueToString from "../../utils/objectConverter";
import { createDynamicColumns } from "../../utils/dataGridColumnUtils";
import { getObjectNameByCode } from "../../utils/objectDataUtils";
import { createAndDownloadExcel, generateExcelFilename } from "../../utils/excelExportUtils";

const getObjectData = () => {
  try {
    const objectDataStr = localStorage.getItem("objectData");
    return objectDataStr ? JSON.parse(objectDataStr) : [];
  } catch (error) {
    console.error("localStorage'dan objectData okunamadı:", error);
    return [];
  }
};

export const getColumns = (t, onlineIds = []) => {
  const objectData = getObjectData();
  const alarmFields = ["5003", "5004", "5005", "5006", "5007", "5008", "5009", "5010"];
  const basicFields = ["2", "4", "6", "7", "18"];

  // Dinamik basic field kolonları oluştur
  const basicColumns = createDynamicColumns(basicFields, objectData);

  // Dinamik alarm kolonları oluştur - custom renderCell ile
  const alarmColumns = alarmFields.map((fieldId) => {
    const columnName = getObjectNameByCode(objectData, fieldId);

    return {
      field: `field_${fieldId}`,
      headerName: columnName,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 150,
      type: "singleSelect",
      valueOptions: [
        { value: 2, label: t("dashboard.statusChip.alarm") || "Alarm" },
        { value: 1, label: t("dashboard.statusChip.warning") || "Uyarı" },
        { value: 0, label: t("dashboard.statusChip.normal") || "Normal" },
      ],
      valueGetter: (_value, row) => {
        const alarmLevel = row[`field_${fieldId}_alarmLevel`];
        return alarmLevel !== undefined ? alarmLevel : null;
      },
      sortComparator: (v1, v2) => {
        const a = v1 ?? -1;
        const b = v2 ?? -1;
        return b - a;
      },
      renderCell: (params) => {
        const value = params.row[`field_${fieldId}`];

        // "20" field'ı için özel durum - bağlantı durumu
        if (fieldId === "20") {
          // value 1 ise Online, 0 ise Offline
          if (value === "1" || value === 1) {
            return renderOnline("Online", t);
          } else if (value === "0" || value === 0) {
            return renderOffline("Offline", t);
          } else {
            return value || "-";
          }
        }

        // Diğer field'lar için alarm level'a göre chip göster
        const alarmLevel = params.row[`field_${fieldId}_alarmLevel`];

        if (alarmLevel === 2) {
          return renderAlarmm(value || "", t);
        } else if (alarmLevel === 1) {
          return renderWarningg(value || "", t);
        } else if (alarmLevel === 0) {
          return renderNormal(value || "", t);
        } else {
          return value || "-";
        }
      },
    };
  });

  return [
  {
    field: "status",
    headerName: t("places.dataGrid.status"),
    flex: 1,
    minWidth: 100,
    headerAlign: "center",
    align: "center",
    sortable: true,
    type: "singleSelect",
    valueOptions: [
      { value: "Online", label: t("places.online") || "Çevrimiçi" },
      { value: "Offline", label: t("places.offline") || "Çevrimdışı" },
    ],
    valueGetter: (_value, row) => {
      return onlineIds.includes(row.nid) ? "Online" : "Offline";
    },
    sortComparator: (v1, v2) => {
      // Online önce gelsin, sonra Offline
      return v1 === "Online" ? -1 : v2 === "Online" ? 1 : 0;
    },
    renderCell: (params) =>
      onlineIds.includes(params.row.nid)
        ? renderOnline("Online", t)
        : renderOffline("Offline", t),
  },
  {
    field: "nodeName",
    headerName: t("regions.dataGrid.nodeName"),
    headerAlign: "center",
    align: "center",
    flex: 1,
    minWidth: 200,
  },
  {
    field: "region",
    headerName: t("regions.dataGrid.regionName"),
    headerAlign: "center",
    align: "center",
    flex: 1,
    minWidth: 250,
  },
  {
    field: "subRegion",
    headerName: t("regions.dataGrid.parentRegion"),
    headerAlign: "center",
    align: "center",
    flex: 1,
    minWidth: 200,
  },
  {
    field: "terminal",
    headerName: t("regions.dataGrid.terminal"),
    headerAlign: "center",
    align: "center",
    flex: 1,
    minWidth: 200,
  },
  {
    field: "field_71",
    headerName: objectData.find((obj) => obj.idObject === 71)?.name || "Field 71",
    headerAlign: "center",
    align: "center",
    flex: 1,
    minWidth: 200,
    type: "number",
    valueGetter: (value, row) => {
      const fieldValue = row.field_71;
      if (!fieldValue || fieldValue === "-") return null;
      // String değeri sayıya çevir
      const numericValue = parseFloat(fieldValue);
      return isNaN(numericValue) ? null : numericValue;
    },
    renderCell: (params) => {
      const value = params.row.field_71;
      return value && value !== "-" ? `${value} ₺` : value;
    },
  },
  {
    field: "field_72",
    headerName: objectData.find((obj) => obj.idObject === 72)?.name || "Field 72",
    headerAlign: "center",
    align: "center",
    flex: 1,
    minWidth: 200,
    type: "number",
    valueGetter: (value, row) => {
      const fieldValue = row.field_72;
      if (!fieldValue || fieldValue === "-") return null;
      // String değeri sayıya çevir
      const numericValue = parseFloat(fieldValue);
      return isNaN(numericValue) ? null : numericValue;
    },
    renderCell: (params) => {
      const value = params.row.field_72;
      return value && value !== "-" ? `${value} ₺` : value;
    },
  },
  {
    field: "alarm",
    headerName: t("regions.dataGrid.alarm"),
    headerAlign: "center",
    align: "center",
    flex: 1,
    minWidth: 200,
    type: "singleSelect",
    valueOptions: [
      { value: 2, label: t("dashboard.statusChip.alarm") || "Alarm" },
      { value: 1, label: t("dashboard.statusChip.warning") || "Uyarı" },
      { value: 0, label: t("dashboard.statusChip.normal") || "Normal" },
    ],
    valueGetter: (_value, row) => {
      return row.alarmStatus !== undefined && row.alarmStatus !== -1 ? row.alarmStatus : null;
    },
    sortComparator: (v1, v2) => {
      const a = v1 ?? -1;
      const b = v2 ?? -1;
      return b - a;
    },
    renderCell: (params) => {
      const alarmStatus = params.row.alarmStatus;
      if (alarmStatus === 2) {
        return renderAlarmm("", t);
      } else if (alarmStatus === 1) {
        return renderWarningg("", t);
      } else if (alarmStatus === 0) {
        return renderNormal("", t);
      } else {
        return "-";
      }
    },
  },
  ...alarmColumns,
  ...basicColumns,
];
};

export default function RegionsDataGrid() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [regions, setRegions] = useState([]);
  const [nodesWithStatus, setNodesWithStatus] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [onlineNodeIds, setOnlineNodeIds] = useState([]);

  // Read event filter from URL query params
  const eventFilter = searchParams.get("event");
  const ALARM_FIELDS = ["5003", "5004", "5005", "5006", "5007", "5008", "5009", "5010"];

  const onlineIds = useMemo(() => onlineNodeIds, [onlineNodeIds]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regionsData, nodesWithStatusData, nodesData, devicesData, onlineNodesData] = await Promise.all([
        getRegion(),
        getNodeWithStatus(),
        getNode(),
        getDevices(),
        getOnlineNodesAPI(),
      ]);
      setRegions(regionsData);
      setNodesWithStatus(nodesWithStatusData);
      setNodes(nodesData);
      setDevices(devicesData || []);
      const onlineIds = (onlineNodesData || []).map((node) => node.idNode);
      setOnlineNodeIds(onlineIds);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds with manual controls
  const { pause, resume, reset } = useInterval(fetchData, 30000, {
    immediate: false,
    enabled: true
  });

  // Handle pause/resume
  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      resume();
      setIsPaused(false);
    } else {
      pause();
      setIsPaused(true);
    }
  }, [isPaused, pause, resume]);

  // Handle manual refresh (resets the interval timer)
  const handleManualRefresh = useCallback(() => {
    fetchData();
    reset();
  }, [fetchData, reset]);

  const rows = useMemo(
    () =>
      nodesWithStatus.map((nodeWithStatus) => {
        const nid = nodeWithStatus.NID;
        const nodeName = nodeWithStatus.NodeName;

        // NID ile getNode listesinden node bilgisini bul (NID = idNode)
        const nodeDetail = nodes.find((n) => n.idNode === nid);

        // NID ile getDevices listesinden device bilgisini bul (NID = node_idNode)
        const device = devices.find((d) => Number(d.node_idNode) === Number(nid));
        const deviceName = device?.nameDevice || device?.serialDevice || "-";

        // Alarm status kontrolü - nodeWithStatus verisindeki tüm alarmLevel değerlerini kontrol et
        let alarmStatus = -1; // Başlangıç değeri
        const alarmFields = ["5003", "5004", "5005", "5006", "5007", "5008", "5009", "5010"];

        for (const field of alarmFields) {
          const fieldData = nodeWithStatus[field];
          if (fieldData && typeof fieldData === "object" && fieldData.alamLevel !== undefined) {
            const level = fieldData.alamLevel;
            // En yüksek alarm level'ı bul
            if (level === 2) {
              alarmStatus = 2; // Alarm bulundu, döngüden çık
              break;
            } else if (level === 1 && alarmStatus !== 2) {
              alarmStatus = 1; // Warning
            } else if (level === 0 && alarmStatus === -1) {
              alarmStatus = 0; // Normal (sadece henüz alarm veya warning bulunmadıysa)
            }
          }
        }

        // Node'un region_idRegion değerini al
        const regionId = nodeDetail?.region_idRegion;

        // region_idRegion ile regions listesinden region bilgisini bul
        const region = regionId
          ? regions.find((r) => r.idRegion === regionId)
          : null;

        const regionName = region?.name || "-";

        // Parent region'ı bul (region kolonu için)
        const parentRegionId = region?.parentRegion;
        const parentRegion = parentRegionId
          ? regions.find((r) => r.idRegion === parentRegionId)
          : null;

        const parentRegionName = parentRegion?.name || "-";

        // Basic ve Alarm field'larının değerlerini al ve convert et
        const objectData = getObjectData();
        const fieldValues = {};
        const basicFields = ["2", "4", "6", "7", "18"];
        const salesFields = ["71", "72"]; // Sales today ve sales yesterday

        // Basic field'ları işle
        for (const fieldId of basicFields) {
          const rawValue = nodeWithStatus[fieldId];
          const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));

          if (rawValue !== undefined && rawValue !== null) {
            // type'a göre değeri convert et
            if (objectInfo && objectInfo.type !== undefined) {
              const convertedValue = ConvertOIDValueToString(
                { Type: objectInfo.type },
                rawValue
              );
              fieldValues[`field_${fieldId}`] = convertedValue;
            } else {
              fieldValues[`field_${fieldId}`] = rawValue;
            }
          } else {
            fieldValues[`field_${fieldId}`] = "-";
          }
        }

        // Sales field'ları işle (71, 72)
        for (const fieldId of salesFields) {
          const rawValue = nodeWithStatus[fieldId];
          const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));

          if (rawValue !== undefined && rawValue !== null) {
            // type'a göre değeri convert et
            if (objectInfo && objectInfo.type !== undefined) {
              const convertedValue = ConvertOIDValueToString(
                { Type: objectInfo.type },
                rawValue
              );
              fieldValues[`field_${fieldId}`] = convertedValue;
            } else {
              fieldValues[`field_${fieldId}`] = rawValue;
            }
          } else {
            fieldValues[`field_${fieldId}`] = "-";
          }
        }

        // Alarm field'ları işle
        for (const fieldId of alarmFields) {
          const fieldData = nodeWithStatus[fieldId];
          const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));

          if (fieldData && typeof fieldData === "object" && fieldData.val !== undefined) {
            const rawValue = fieldData.val;
            // type'a göre değeri convert et
            if (objectInfo && objectInfo.type !== undefined) {
              const convertedValue = ConvertOIDValueToString(
                { Type: objectInfo.type },
                rawValue
              );
              fieldValues[`field_${fieldId}`] = convertedValue;
            } else {
              fieldValues[`field_${fieldId}`] = rawValue;
            }
            // Alarm level'ı da sakla
            fieldValues[`field_${fieldId}_alarmLevel`] = fieldData.alamLevel;
          } else {
            fieldValues[`field_${fieldId}`] = "-";
            fieldValues[`field_${fieldId}_alarmLevel`] = undefined;
          }
        }

        return {
          id: nid,
          nid: nid,
          nodeName: nodeName,
          terminal: deviceName,
          alarmStatus: alarmStatus,
          region: parentRegionName,
          subRegion: regionName,
          ...fieldValues,
        };
      }),
    [nodesWithStatus, nodes, devices, regions]
  );

  // Filter rows by event alarm/warning status from URL query
  const eventFilterName = useMemo(() => {
    if (!eventFilter || !ALARM_FIELDS.includes(eventFilter)) return null;
    const objectData = getObjectData();
    const obj = objectData.find((o) => o.idObject === parseInt(eventFilter));
    return obj?.name || `Event ${eventFilter}`;
  }, [eventFilter]);

  const displayRows = useMemo(() => {
    if (!eventFilter || !ALARM_FIELDS.includes(eventFilter)) return rows;
    const filtered = rows.filter((r) => {
      const level = r[`field_${eventFilter}_alarmLevel`];
      return level === 1 || level === 2;
    });
    return filtered.length > 0 ? filtered : rows;
  }, [rows, eventFilter]);

  const isFiltered = eventFilterName && displayRows.length < rows.length;

  const handleClearFilter = useCallback(() => {
    searchParams.delete("event");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleExportToExcel = useCallback(() => {
    const objectData = getObjectData();
    const alarmFields = ["5003", "5004", "5005", "5006", "5007", "5008", "5009", "5010"];
    const basicFields = ["2", "4", "6", "7", "18"];

    const exportColumns = [
      { field: "status", headerName: t("places.dataGrid.status") },
      { field: "nodeName", headerName: t("regions.dataGrid.nodeName") },
      { field: "region", headerName: t("regions.dataGrid.regionName") },
      { field: "subRegion", headerName: t("regions.dataGrid.parentRegion") },
      { field: "terminal", headerName: t("regions.dataGrid.terminal") },
      { field: "field_71", headerName: objectData.find((obj) => obj.idObject === 71)?.name || "Field 71" },
      { field: "field_72", headerName: objectData.find((obj) => obj.idObject === 72)?.name || "Field 72" },
      ...alarmFields.map((fieldId) => ({
        field: `field_${fieldId}`,
        headerName: getObjectNameByCode(objectData, fieldId),
      })),
      ...basicFields.map((fieldId) => ({
        field: `field_${fieldId}`,
        headerName: getObjectNameByCode(objectData, fieldId),
      })),
    ];

    const exportRows = displayRows.map((row) => ({
      ...row,
      status: onlineIds.includes(row.nid) ? "Online" : "Offline",
    }));

    const fileName = generateExcelFilename(t("places.excelPrefix") || "Otomatlar");
    createAndDownloadExcel(exportRows, exportColumns, t("places.excelSheet") || "Otomatlar", fileName);
  }, [displayRows, onlineIds, t]);

  const handleRowClick = (params) => {
    const nid = params.row.nid;
    window.open(`/vending-machines/${nid}`, "_blank");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Control Panel */}
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", border: 1, borderColor: "divider" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Statistics Cards */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1 }}
          >
            <Stack direction="column" spacing={0.5} alignItems="center">
              <Box sx={{
                px: 2,
                py: 0.5,
                borderRadius: 1,
                bgcolor: "rgba(25, 118, 210, 0.1)",
                border: 1.5,
                borderColor: "primary.main",
                color: "primary.main",
                minWidth: 60,
                textAlign: "center"
              }}>
                <Typography variant="h6" fontWeight="bold">
                  {displayRows.length}
                </Typography>
              </Box>
              <Typography variant="caption" fontSize="0.75rem" color="text.secondary">
                {t("dashboard.statusChip.vendingMachine")}
              </Typography>
            </Stack>

            <Stack direction="column" spacing={0.5} alignItems="center">
              <Box sx={{
                px: 2,
                py: 0.5,
                borderRadius: 1,
                bgcolor: "rgba(211, 47, 47, 0.1)",
                border: 1.5,
                borderColor: "error.main",
                color: "error.main",
                minWidth: 60,
                textAlign: "center"
              }}>
                <Typography variant="h6" fontWeight="bold">
                  {displayRows.filter(r => r.alarmStatus === 2).length}
                </Typography>
              </Box>
              <Typography variant="caption" fontSize="0.75rem" color="text.secondary">
                {t("dashboard.statusChip.alarm")}
              </Typography>
            </Stack>

            <Stack direction="column" spacing={0.5} alignItems="center">
              <Box sx={{
                px: 2,
                py: 0.5,
                borderRadius: 1,
                bgcolor: "rgba(230, 167, 0, 0.1)",
                border: 1.5,
                borderColor: "#E6A700",
                color: "#E6A700",
                minWidth: 60,
                textAlign: "center"
              }}>
                <Typography variant="h6" fontWeight="bold">
                  {displayRows.filter(r => r.alarmStatus === 1).length}
                </Typography>
              </Box>
              <Typography variant="caption" fontSize="0.75rem" color="text.secondary">
                {t("dashboard.statusChip.warning")}
              </Typography>
            </Stack>

            <Stack direction="column" spacing={0.5} alignItems="center">
              <Box sx={{
                px: 2,
                py: 0.5,
                borderRadius: 1,
                bgcolor: "rgba(46, 125, 50, 0.1)",
                border: 1.5,
                borderColor: "success.main",
                color: "success.main",
                minWidth: 60,
                textAlign: "center"
              }}>
                <Typography variant="h6" fontWeight="bold">
                  {displayRows.filter(r => r.alarmStatus === 0).length}
                </Typography>
              </Box>
              <Typography variant="caption" fontSize="0.75rem" color="text.secondary">
                {t("dashboard.statusChip.normal")}
              </Typography>
            </Stack>
          </Stack>

          {/* Controls */}
          <Stack
            direction="column"
            spacing={1}
            alignItems="center"
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            {lastRefresh && (
              <Typography variant="caption" color="text.secondary" textAlign="center">
                {t("lastRefresh") || "Last refresh"}: {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 0 }}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <Button
                onClick={handlePauseResume}
                startIcon={isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                color={isPaused ? "success" : "warning"}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  borderRadius: { xs: 1, sm: 0 },
                  borderTopLeftRadius: { sm: 1 },
                  borderBottomLeftRadius: { sm: 1 },
                  borderRight: { sm: "none" }
                }}
              >
                {isPaused ? (t("resume") || "Resume") : (t("pause") || "Pause")}
              </Button>
              <Button
                onClick={handleManualRefresh}
                startIcon={<RefreshIcon />}
                disabled={loading}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  borderRadius: { xs: 1, sm: 0 },
                  borderTopRightRadius: { sm: 1 },
                  borderBottomRightRadius: { sm: 1 },
                }}
              >
                {t("refresh") || "Refresh"}
              </Button>
            </Stack>
            <Button
              onClick={handleExportToExcel}
              startIcon={<FileDownloadIcon />}
              disabled={displayRows.length === 0}
              variant="contained"
              color="success"
              size="medium"
              sx={{ borderRadius: 2, color: "white", whiteSpace: "nowrap" }}
            >
              {t("places.exportExcel") || "Dışarı Aktar"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Active Filter Banner */}
      {isFiltered && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1 }}>
          <FilterListIcon fontSize="small" color="primary" />
          <Typography variant="body2" color="text.secondary">
            {eventFilterName}:
          </Typography>
          <Chip
            label={t("dashboard.statusChip.alarm")}
            size="small"
            sx={{ bgcolor: "rgba(211, 47, 47, 0.1)", color: "error.main", fontWeight: 600 }}
          />
          <Chip
            label={t("dashboard.statusChip.warning")}
            size="small"
            sx={{ bgcolor: "rgba(230, 167, 0, 0.1)", color: "#E6A700", fontWeight: 600 }}
          />
          <Typography variant="body2" color="text.secondary">
            ({displayRows.length}/{rows.length})
          </Typography>
          <Chip
            label={t("dashboard.topicHeader.all") || "Tümü"}
            size="small"
            variant="outlined"
            onDelete={handleClearFilter}
            onClick={handleClearFilter}
          />
        </Box>
      )}

      {/* Data Grid */}
      <Box sx={{ height: 630, width: "100%", position: "relative" }}>
        {loading && rows.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error" align="center" sx={{ my: 2 }}>
            {error}
          </Typography>
        )}
        <DataGrid
          rows={displayRows}
          columns={getColumns(t, onlineIds)}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
            sorting: {
              sortModel: [{ field: 'status', sort: 'asc' }],
            },
          }}
          pageSizeOptions={[15, 20, 50]}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
          }}
        />
      </Box>
    </Box>
  );
}
